import { useEffect, useState } from 'react';
import moment from 'moment';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { IContaBancaria, IMinutaBancaria, IMinutaLoteResumo } from '../../interfaces/IModuloBancario';
import ModuloBancarioService from '../../services/ModuloBancarioService';

interface Props {
    esteiraId: number;
    ano: number;
    mes: number;
    minutaIds: number[];
    minutas: IMinutaBancaria[];
    contas: IContaBancaria[];
    onHide: () => void;
    onAprovado: () => void;
}

const brl = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const descreverConta = (c?: IContaBancaria) =>
    c ? `${c.banco} / ${c.agencia ? `${c.agencia} / ` : ''}${c.conta}${c.descricao ? ` — ${c.descricao}` : ''}` : '—';
// O id da minuta não aparece na tabela: o analista reconhece a linha pela data e pelo histórico.
const descreverMinuta = (m: IMinutaBancaria | undefined, id: number) => {
    const linha = m?.linhas[0];
    return linha ? `${moment(linha.data).format('DD/MM/YYYY')} · ${linha.historico ?? '—'}` : `Minuta ${id}`;
};

// DEVN3-770 — prévia do lote: o servidor recalcula os impedimentos, aqui e de novo ao aprovar.
export default function ModalAprovarLote({ esteiraId, ano, mes, minutaIds, minutas, contas, onHide, onAprovado }: Props) {
    const [previa, setPrevia] = useState<IMinutaLoteResumo | null>(null);
    const [resultado, setResultado] = useState<IMinutaLoteResumo | null>(null);
    const [ciencia, setCiencia] = useState(false);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        ModuloBancarioService.previaLote(esteiraId, ano, mes, minutaIds)
            .then(setPrevia)
            .catch((err: any) => {
                Swal.fire({ text: err?.message ?? 'Não foi possível montar a prévia do lote.', icon: 'error' });
                onHide();
            });
        // A prévia vale para o lote com que o modal abriu.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const aprovar = async () => {
        setSalvando(true);
        try {
            const aprovado = await ModuloBancarioService.aprovarLote(esteiraId, ano, mes, minutaIds, ciencia);
            setResultado(aprovado);
            onAprovado();
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível aprovar o lote.', icon: 'error' });
        } finally {
            setSalvando(false);
        }
    };

    const resumo = resultado ?? previa;

    return (
        <Modal show onHide={onHide} centered size='lg'>
            <Modal.Header closeButton>
                <Modal.Title>{resultado ? 'Lote aprovado' : 'Aprovar em lote'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!resumo ? (
                    <div className='d-flex align-items-center gap-2 text-muted py-4'>
                        <span className='spinner-border spinner-border-sm'></span>Conferindo as minutas...
                    </div>
                ) : (
                    <>
                        <div className='mb-4'>
                            {resultado ? 'Aprovadas' : 'Serão aprovadas'} <strong>{resumo.aprovaveis}</strong> de {resumo.selecionadas} minuta(s) selecionada(s),
                            no total de <strong>{brl(resumo.valor)}</strong>, competência {resumo.competencia.split('-').reverse().join('/')}.
                        </div>
                        {resumo.porConta.length > 0 && (
                            <table className='table table-sm fs-7 mb-4'>
                                <thead>
                                    <tr className='text-muted'>
                                        <th>Conta bancária</th>
                                        <th className='text-end'>Minutas</th>
                                        <th className='text-end'>Valor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resumo.porConta.map(p => (
                                        <tr key={p.contaBancariaId}>
                                            <td>{descreverConta(contas.find(c => c.id === p.contaBancariaId))}</td>
                                            <td className='text-end'>{p.quantidade}</td>
                                            <td className='text-end'>{brl(p.valor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {resumo.impedidas.length > 0 && (
                            <div className='mb-4'>
                                <div className='fw-bold text-danger mb-2'>{resumo.impedidas.length} minuta(s) fora do lote por impedimento</div>
                                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {resumo.impedidas.map(i => (
                                        <div key={i.minutaId} className='fs-7 mb-1'>
                                            <strong>{descreverMinuta(minutas.find(m => m.id === i.minutaId), i.minutaId)}:</strong> {i.motivos.join(' ')}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {!resultado && resumo.aprovaveis > 0 && (
                            <label className='form-check form-check-custom form-check-solid align-items-start gap-2 p-3 border rounded cursor-pointer'>
                                <input type='checkbox' className='form-check-input' checked={ciencia} onChange={e => setCiencia(e.target.checked)} />
                                <span className='fs-7'>{resumo.declaracaoCiencia}</span>
                            </label>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className='btn btn-light' onClick={onHide}>{resultado ? 'Fechar' : 'Cancelar'}</button>
                {!resultado && (
                    <button className='btn btn-primary' disabled={!previa || previa.aprovaveis === 0 || !ciencia || salvando} onClick={aprovar}>
                        {salvando && <span className='spinner-border spinner-border-sm me-2'></span>}
                        Aprovar {previa ? previa.aprovaveis : ''} minuta(s)
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
